import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded';
import LocalLaundryServiceRoundedIcon from '@mui/icons-material/LocalLaundryServiceRounded';
import StorePicker from './StorePicker';
import { CATEGORY_LABELS } from '../navigation';
import { colors, layout, radius } from '../design-system/tokens';

const CATEGORY_ICONS = {
  carwash: StorefrontRoundedIcon,
  academy: SchoolRoundedIcon,
  gym: FitnessCenterRoundedIcon,
  laundry: LocalLaundryServiceRoundedIcon,
};

const formatToday = () =>
  new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  }).format(new Date());

const HEADER_SIZES = {
  icon: 40,
  iconGlyph: 20,
  statusChip: 24,
  control: layout.headerControlHeight,
};

const StoreStatusHeader = ({
  session,
  storeOptions = [],
  selectedStoreId,
  recentStoreIds = [],
  masterQaMode,
  onStoreChange,
}) => {
  const category = session?.store?.category;
  const Icon = CATEGORY_ICONS[category] || StorefrontRoundedIcon;

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 8,
        bgcolor: colors.surface,
        borderBottom: `1px solid ${masterQaMode ? colors.qa.border : colors.border.muted}`,
        boxShadow: '0 1px 0 rgba(35, 42, 52, 0.04)',
      }}
    >
      <Box
        sx={{
          maxWidth: layout.contentMaxWidth,
          mx: 'auto',
          px: { xs: 2, sm: 2.5, lg: layout.pagePaddingX },
          minHeight: { xs: 'auto', sm: 64 },
          py: { xs: 1.25, sm: 1 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
          alignItems: 'center',
          gap: { xs: 1.35, md: 2 },
        }}
      >
        <Stack
          direction="row"
          spacing={1.15}
          sx={{ alignItems: 'center', minWidth: 0 }}
        >
          <Box
            sx={{
              width: HEADER_SIZES.icon,
              height: HEADER_SIZES.icon,
              borderRadius: `${radius.button}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: colors.brand.primary,
              color: colors.text.inverse,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: HEADER_SIZES.iconGlyph }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.85}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.65 }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: { xs: 16.5, sm: 17.5 },
                  lineHeight: 1.18,
                  fontWeight: 800,
                  overflowWrap: 'anywhere',
                }}
              >
                {session.store.name}
              </Typography>
              <Chip
                size="small"
                label={CATEGORY_LABELS[category] || category}
                sx={{
                  height: HEADER_SIZES.statusChip,
                  borderRadius: `${radius.chip}px`,
                  bgcolor: colors.status.neutral.bg,
                  color: colors.status.neutral.fg,
                  border: `1px solid ${colors.status.neutral.border}`,
                  fontSize: 12,
                  fontWeight: 800,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
              <Chip
                size="small"
                label="운영 중"
                sx={{
                  height: HEADER_SIZES.statusChip,
                  borderRadius: `${radius.chip}px`,
                  bgcolor: colors.status.success.bg,
                  color: colors.status.success.fg,
                  border: `1px solid ${colors.status.success.border}`,
                  fontSize: 12,
                  fontWeight: 800,
                  '& .MuiChip-label': { px: 1.05 },
                  '&:before': {
                    content: '""',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: colors.status.success.fg,
                    ml: 0.8,
                    mr: -0.15,
                  },
                }}
              />
            </Stack>
          </Box>
        </Stack>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: { xs: 0.9, md: 1.1 },
            rowGap: 0.8,
            minWidth: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.text.muted,
              display: { xs: 'none', md: 'block' },
              whiteSpace: 'nowrap',
              fontSize: 13,
              lineHeight: `${HEADER_SIZES.control}px`,
            }}
          >
            {formatToday()}
          </Typography>
          {masterQaMode && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: { xs: 0.8, sm: 0.9 },
                width: { xs: '100%', sm: 'auto' },
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                  minWidth: 0,
                  flexWrap: 'wrap',
                  rowGap: 0.65,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Chip
                  size="small"
                  label="Master QA"
                  sx={{
                    height: HEADER_SIZES.control,
                    borderRadius: `${radius.button}px`,
                    bgcolor: colors.canvas.subtle,
                    color: colors.text.secondary,
                    border: `1px solid ${colors.border.soft}`,
                    fontSize: 12.5,
                    fontWeight: 800,
                    '& .MuiChip-label': { px: 1.15 },
                  }}
                />
                <Chip
                  size="small"
                  label="QA로 보는 중"
                  sx={{
                    height: HEADER_SIZES.control,
                    borderRadius: `${radius.button}px`,
                    bgcolor: colors.qa.bg,
                    color: colors.qa.fg,
                    border: `1px solid ${colors.qa.border}`,
                    fontSize: 12.5,
                    fontWeight: 800,
                    '& .MuiChip-label': { px: 1.15 },
                  }}
                />
              </Stack>
              {storeOptions.length > 0 && (
                <Box sx={{ minWidth: 0, width: { xs: '100%', sm: 280 } }}>
                  <StorePicker
                    compact
                    options={storeOptions}
                    value={selectedStoreId || session.store.id}
                    recentStoreIds={recentStoreIds}
                    onChange={onStoreChange}
                  />
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default StoreStatusHeader;
